/**
 * DIEUKYDIEU.TV - MEDIA VIEW CONTROLLER (JS/MEDIA.JS)
 * Cascading Apple Segmented Filter Chips & Rich Media Renderer (< 220 lines)
 */

import { mediaCategories, musicList, videoList, audioMediaList, galleryList } from './media-data.js';
import { t } from './i18n.js';
import { playAudio } from './player.js';
import { renderCardHTML, renderChipHTML, setActiveChip, renderEmptyStateHTML } from './components/index.js';
import { initChipScroll } from './chip-scroll.js';
import {
    openVideoCinema,
    closeVideoCinema,
    openMediaLightbox,
    closeMediaLightbox,
    navigateMediaLightbox,
    initMediaModalEvents
} from './media-modal.js';

let activeTier1 = 'music';
let activeTier2 = 'all';

export function renderMediaTier1Chips() {
    const container = document.getElementById('media-tier1-chips');
    if (!container) return;
    const keys = ['music', 'video', 'audio', 'gallery'];
    container.innerHTML = keys.map((k, idx) => {
        const cat = mediaCategories[k];
        const isActive = (k === activeTier1);
        const hasSub = cat && cat.sub && cat.sub.length > 0;
        const label = t(`media_cat_${k}`) || cat?.name || k;
        return renderChipHTML({
            id: k,
            label,
            icon: cat?.icon || '',
            isActive,
            hasSubmenu: hasSub,
            variant: 'pill',
            onClickAttr: `filterMedia('${k}', this)`,
            staggerIndex: idx,
            legacyClass: 'media-chip'
        });
    }).join('');
    initChipScroll(container);
}

export function renderMediaTier2Chips(tier1Type) {
    const container = document.getElementById('media-sub-chips');
    const accordion = document.getElementById('media-tier2-accordion');
    if (!container || !accordion) return;
    const catData = mediaCategories[tier1Type];
    if (!catData || !catData.sub || catData.sub.length === 0) {
        accordion.classList.remove('expanded');
        accordion.setAttribute('aria-hidden', 'true');
        container.innerHTML = '';
        return;
    }
    accordion.classList.add('expanded');
    accordion.setAttribute('aria-hidden', 'false');

    container.innerHTML = catData.sub.map((item, idx) => renderChipHTML({
        id: item.id,
        label: item.name,
        isActive: item.id === activeTier2,
        variant: 'subchip',
        onClickAttr: `filterMediaSubCategory('${item.id}', this)`,
        staggerIndex: idx,
        legacyClass: 'media-sub-chip'
    })).join('');
    initChipScroll(container);
}

function updateFilterStatusBar(totalRendered = 0) {
    const pathText = document.getElementById('media-filter-path-text');
    const countBadge = document.getElementById('media-filtered-count-badge');
    const totalBadge = document.getElementById('media-total-badge');
    const unit = t('media_works_unit') || 'tác phẩm';
    if (pathText) {
        const t1Name = t(`media_cat_${activeTier1}`) || mediaCategories[activeTier1]?.name || 'Âm nhạc';
        const subName = mediaCategories[activeTier1]?.sub?.find(s => s.id === activeTier2)?.name;
        pathText.innerText = (subName && activeTier2 !== 'all') 
            ? `${t('nav_media') || 'Media'} › ${t1Name} › ${subName}` 
            : `${t('nav_media') || 'Media'} › ${t1Name}`;
    }
    if (countBadge) countBadge.innerText = `${totalRendered} ${unit}`;
    if (totalBadge) totalBadge.innerText = `${mediaCategories[activeTier1]?.count || totalRendered} ${unit}`;
}

export function renderMedia() {
    const container = document.getElementById('media-list-container');
    if (!container) return;
    container.className = `media-grid media-grid-${activeTier1}`;
    let itemsHtml = '';
    let totalCount = 0;

    if (activeTier1 === 'all' || activeTier1 === 'music') {
        const filteredMusic = musicList.filter(m => activeTier2 === 'all' || m.subCategory === activeTier2);
        totalCount += filteredMusic.length;
        itemsHtml += filteredMusic.map((m, idx) => renderCardHTML({
            id: m.id,
            title: m.title,
            artist: m.artist,
            img: m.thumb || m.img,
            duration: m.duration,
            kicker: m.tag,
            variant: 'media-music',
            legacyClass: 'media-card media-card-music',
            onClickAttr: m.embedUrl ? `openVideoCinema('${m.id}')` : `playAudio(${m.audioIndex ?? 0})`,
            staggerIndex: idx
        })).join('');
    }

    if (activeTier1 === 'all' || activeTier1 === 'video') {
        const filteredVideos = videoList.filter(v => activeTier2 === 'all' || v.subCategory === activeTier2);
        totalCount += filteredVideos.length;
        itemsHtml += filteredVideos.map((v, idx) => renderCardHTML({
            id: v.id,
            title: v.title,
            artist: v.artist,
            img: v.thumb,
            duration: v.duration,
            kicker: v.tag,
            variant: 'media-video',
            legacyClass: 'media-card media-card-video',
            onClickAttr: `openVideoCinema('${v.id}')`,
            staggerIndex: idx
        })).join('');
    }

    if (activeTier1 === 'all' || activeTier1 === 'audio') {
        const filteredAudios = audioMediaList.filter(a => activeTier2 === 'all' || a.subCategory === activeTier2);
        totalCount += filteredAudios.length;
        itemsHtml += filteredAudios.map((t, idx) => renderCardHTML({
            id: t.id,
            title: t.title,
            artist: t.artist,
            img: t.img,
            duration: t.duration,
            kicker: t.tag,
            variant: 'media-audio',
            legacyClass: 'media-card media-card-audio',
            onClickAttr: `playAudio(${t.audioIndex})`,
            staggerIndex: idx
        })).join('');
    }

    if (activeTier1 === 'all' || activeTier1 === 'gallery') {
        const filteredGallery = galleryList.filter(g => activeTier2 === 'all' || g.subCategory === activeTier2);
        totalCount += filteredGallery.length;
        itemsHtml += filteredGallery.map((g, idx) => renderCardHTML({
            id: g.id,
            title: g.title,
            author: g.author,
            desc: g.description,
            img: g.img,
            kicker: g.tag,
            variant: 'media-gallery',
            legacyClass: 'media-card media-card-gallery',
            onClickAttr: `openMediaLightbox('${g.id}')`,
            staggerIndex: idx
        })).join('');
    }

    container.innerHTML = itemsHtml || renderEmptyStateHTML({
        icon: 'folder_open',
        title: 'Chưa có tác phẩm nào',
        desc: 'Vui lòng chọn danh mục con khác để khám phá các nội dung đặc sắc.',
        actionText: 'Xem tất cả ' + (mediaCategories[activeTier1]?.name || 'Âm nhạc'),
        actionOnClickAttr: `filterMedia('${activeTier1}', null, 'all')`
    });
    updateFilterStatusBar(totalCount);
}

export function filterMedia(tier1Type = 'music', btn = null, subCat = 'all') {
    activeTier1 = (tier1Type === 'all' || !tier1Type) ? 'music' : tier1Type;
    activeTier2 = subCat || 'all';
    renderMediaTier1Chips();
    renderMediaTier2Chips(activeTier1);
    renderMedia();
    if (typeof window.updateSidebarActive === 'function') {
        window.updateSidebarActive('media-view', null, activeTier1, activeTier2);
    }
}

export function filterMediaSubCategory(subCat = 'all', btn = null) {
    activeTier2 = subCat;
    const subContainer = document.getElementById('media-sub-chips');
    if (subContainer) {
        setActiveChip(subContainer, btn || subCat, '.sub-chip');
    }
    renderMedia();
    if (typeof window.updateSidebarActive === 'function') {
        window.updateSidebarActive('media-view', null, activeTier1, activeTier2);
    }
}

export function initMediaView() {
    renderMediaTier1Chips();
    renderMediaTier2Chips(activeTier1);
    renderMedia();
    initMediaModalEvents();
}

export { openVideoCinema, closeVideoCinema, openMediaLightbox, closeMediaLightbox, navigateMediaLightbox };
